import { useContext, useEffect, useState } from 'react';
import AppModal, { AppModalProps } from '@/components/AppModal/AppModal';
import classNames from './OfflineNotification.module.scss';
import TheButton from '@/components/TheButton/TheButton';
import coneSvgRaw from '@/assets/svg/cone.svg?raw';
import { useTranslation } from 'react-i18next';
import { DispatcherEvents, EventDispatcherContext } from '@/context/eventDispatcherContext';

// Bundled inline so the icon works when the browser is offline
const CONE_ICON = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(coneSvgRaw)}`;

// The browser's offline event can fire on momentary blips (network interface
// handoffs, sleep/wake, backgrounded tab throttling) even though the user
// never actually lost connectivity. Wait to see if it's still offline after
// this delay before showing the modal, so a quick online event can cancel it.
const OFFLINE_DEBOUNCE_MS = 2000;

export type OfflineNotificationProps = Omit<AppModalProps, 'show' | 'children'>;

const OfflineNotification = ({
  modalContentClassName,
  showBackdrop = true,
  type = 'fade',
  ...rest
}: OfflineNotificationProps) => {
  const { t } = useTranslation();
  const dispatcher = useContext(EventDispatcherContext);

  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine
  );
  const [needsAcknowledgement, setNeedsAcknowledgement] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine
  );

  useEffect(() => {
    let offlineTimer: ReturnType<typeof setTimeout> | null = null;

    const clearOfflineTimer = () => {
      if (offlineTimer !== null) {
        clearTimeout(offlineTimer);
        offlineTimer = null;
      }
    };

    const onWindowOffline = () => {
      clearOfflineTimer();
      offlineTimer = setTimeout(() => {
        offlineTimer = null;
        if (!navigator.onLine) {
          setIsOffline(true);
          setNeedsAcknowledgement(true);
          dispatcher.dispatch(DispatcherEvents.OFFLINE_POPUP_OPEN, undefined);
        }
      }, OFFLINE_DEBOUNCE_MS);
    };

    const onWindowOnline = () => {
      clearOfflineTimer();
      setIsOffline(false);
    };

    window.addEventListener('offline', onWindowOffline);
    window.addEventListener('online', onWindowOnline);

    return () => {
      clearOfflineTimer();
      window.removeEventListener('offline', onWindowOffline);
      window.removeEventListener('online', onWindowOnline);
    };
  }, [dispatcher]);

  const show = needsAcknowledgement;

  const handleOk = () => {
    dispatcher.dispatch(DispatcherEvents.OFFLINE_POPUP_CLOSE, undefined);
    setNeedsAcknowledgement(false);
  };

  if (!show) {
    return null;
  }

  return (
    <AppModal
      show={show}
      type={type}
      showBackdrop={showBackdrop}
      modalContentClassName={modalContentClassName}
      className={classNames.wrapper}
      {...rest}
    >
      <div className={classNames.modal}>
        <img
          src={CONE_ICON}
          alt=''
          className={classNames.icon}
          width={80}
          height={65}
          draggable={false}
        />

        <h2>{t('offlineNotification.title')}</h2>
        <div className={classNames.description}>{t('offlineNotification.description')}</div>
        <div className={classNames.buttons}>
          <TheButton
            className={`${classNames.confirmBtn}${isOffline ? ` ${classNames.confirmBtnOffline}` : ''}`}
            text={t('offlineNotification.button')}
            shouldRecolor={false}
            disabled={isOffline}
            emitOnClick={handleOk}
          />
        </div>
      </div>
    </AppModal>
  );
};

export default OfflineNotification;
